import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { ClubLevel, PointsSummary, Reward, RewardRedemption } from 'src/app/models/community';
import { CommunityService } from 'src/app/services/community.service';
import { I18nService } from 'src/app/services/i18n.service';
import { formatDate } from '../account-sections';

const LEVEL_ORDER: ClubLevel[] = ['bronze', 'silver', 'gold', 'platinum'];

@Component({
  selector: 'app-rewards-shop',
  templateUrl: './rewards-shop.component.html',
  styleUrls: ['./rewards-shop.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RewardsShopComponent implements OnInit {

  summary: PointsSummary | null = null;
  rewards: Reward[] = [];
  redemptions: RewardRedemption[] = [];
  loading = true;
  error = '';
  redeemingId: string | null = null;

  constructor(
    private communityService: CommunityService,
    private snackBar: MatSnackBar,
    public i18n: I18nService
  ) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      summary: this.communityService.loadPoints(),
      rewards: this.communityService.rewards(),
      redemptions: this.communityService.myRedemptions(),
    }).subscribe({
      next: result => {
        Object.assign(this, result);
        this.loading = false;
      },
      error: err => {
        this.error = err || this.i18n.t('account.loadError');
        this.loading = false;
      }
    });
  }

  levelAllowed(reward: Reward): boolean {
    return !!this.summary && LEVEL_ORDER.indexOf(this.summary.level) >= LEVEL_ORDER.indexOf(reward.minLevel || 'bronze');
  }

  missingPoints(reward: Reward): number {
    return Math.max(0, reward.costPoints - (this.summary?.available || 0));
  }

  progress(reward: Reward): number {
    return Math.min(100, ((this.summary?.available || 0) / reward.costPoints) * 100);
  }

  canRedeem(reward: Reward): boolean {
    return !!this.summary?.enabled && this.levelAllowed(reward) && this.missingPoints(reward) === 0 && reward.stock !== 0 && !this.redeemingId;
  }

  redeem(reward: Reward): void {
    if (!this.canRedeem(reward) || !confirm(this.i18n.t('club.shop.confirm', { points: reward.costPoints, name: reward.name }))) {
      return;
    }
    this.redeemingId = reward._id;
    this.communityService.redeem(reward._id).subscribe({
      next: redemption => {
        this.redeemingId = null;
        this.redemptions = [redemption, ...this.redemptions];
        if (reward.stock !== null) {
          reward.stock = Math.max(0, reward.stock - 1);
        }
        this.communityService.loadPoints().subscribe(summary => this.summary = summary);
        this.snackBar.open(this.i18n.t('club.shop.success'), this.i18n.t('common.ok'), { duration: 5000 });
      },
      error: err => {
        this.redeemingId = null;
        this.snackBar.open(err || this.i18n.t('common.errorOccurred'), this.i18n.t('common.close'), { duration: 5000 });
      }
    });
  }

  copy(code: string): void {
    navigator.clipboard?.writeText(code).then(
      () => this.snackBar.open(this.i18n.t('club.shop.copied'), undefined, { duration: 2000 }),
      () => { }
    );
  }

  isLink(code: string): boolean {
    return /^https?:\/\//i.test(code || '');
  }

  date(value: string): string {
    return formatDate(value, this.i18n.current);
  }
}
